// src/components/SellerSidebar.tsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, 
    Package, 
    PlusCircle, 
    MessageSquareText, 
    ShoppingCart, 
    UserCircle, 
    Settings,
    LifeBuoy,
    X,
    ChevronDown,
    ChevronRight,
    Briefcase,
    Landmark,
    List
} from 'lucide-react';

// --- Interface Definitions ---
interface SubMenuItem {
    name: string;
    path: string;
    icon?: React.ElementType;
}

interface NavItem {
    name: string;
    path?: string;
    icon: React.ElementType;
    submenu?: SubMenuItem[];
}

// --- Navigation Items Configuration ---
const navItems: NavItem[] = [
    { 
        name: 'Dashboard', 
        path: '/seller/dashboard', 
        icon: LayoutDashboard 
    },
    { 
        name: 'Profile', 
        icon: UserCircle,
        submenu: [
            { name: 'User Profile', path: '/seller/profile/user', icon: UserCircle },
            { name: 'Business Profile', path: '/seller/profile/business', icon: Briefcase },
            { name: 'Bank Details', path: '/seller/profile/bank', icon: Landmark },
        ]
    },
    { 
        name: 'Leads & Inquiry', 
        path: '/seller/leads', 
        icon: MessageSquareText 
    },
    { 
        name: 'Manage Products', 
        icon: Package,
        submenu: [
            { name: 'My Products', path: '/seller/product', icon: List },
            { name: 'Add Product', path: '/seller/add-product', icon: PlusCircle },
        ]
    },
    // { 
    //     name: 'Orders', 
    //     path: '/seller/orders', 
    //     icon: ShoppingCart 
    // },
    { 
        name: 'Settings', 
        path: '/seller/settings', 
        icon: Settings 
    },
];

const SellerSidebar: React.FC<{ isOpen: boolean, closeSidebar: () => void }> = ({ isOpen, closeSidebar }) => {
    const location = useLocation();
    
    // State to manage which dropdown is open
    const [openMenu, setOpenMenu] = useState<string | null>(null);

    const toggleMenu = (name: string) => {
        setOpenMenu(openMenu === name ? null : name);
    };

    return (
        <aside 
            className={`fixed left-0 top-0 md:top-[64px] h-full md:h-[calc(100vh-64px)] bg-[#00635e] text-white shadow-2xl transition-transform duration-300 z-40 flex flex-col justify-between w-64
            overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']
            ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
            `}
        >
            <div>
                {/* Mobile Header inside Sidebar (Logo + Close Btn) */}
                <div className="flex items-center justify-between p-4 md:hidden border-b border-teal-700">
                    <span className="font-black text-xl tracking-tight">Trady<span className="text-red-500">Frog</span></span>
                    <button onClick={closeSidebar} className="p-1 hover:bg-white/10 rounded-full">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-4 space-y-2">
                    <p className="text-[10px] uppercase tracking-widest text-teal-200/60 font-bold mb-4 px-3 mt-2 hidden md:block">Menu</p>
                    
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        
                        // Check if this item has a submenu
                        if (item.submenu) {
                            const isChildActive = item.submenu.some(sub => location.pathname === sub.path);
                            const isOpenState = openMenu === item.name || isChildActive;

                            return (
                                <div key={item.name} className="space-y-1">
                                    {/* Parent Button (Toggle) */}
                                    <button
                                        onClick={() => toggleMenu(item.name)}
                                        className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200 group
                                            ${isChildActive 
                                                ? 'bg-teal-800/50 text-white font-bold' 
                                                : 'hover:bg-white/10 text-teal-50'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Icon size={20} className={isChildActive ? 'text-red-400' : ''} />
                                            <span className="text-sm tracking-wide">{item.name}</span>
                                        </div>
                                        {isOpenState ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </button>

                                    {/* Submenu Items */}
                                    {isOpenState && (
                                        <div className="pl-4 space-y-1 bg-teal-900/20 rounded-lg py-1">
                                            {item.submenu.map((subItem) => {
                                                const isSubActive = location.pathname === subItem.path;
                                                const SubIcon = subItem.icon || ChevronRight;
                                                return (
                                                    <Link
                                                        key={subItem.name}
                                                        to={subItem.path}
                                                        className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm
                                                            ${isSubActive 
                                                                ? 'bg-white text-[#00635e] font-bold shadow-sm' 
                                                                : 'text-teal-200 hover:text-white hover:bg-white/5'
                                                            }
                                                        `}
                                                    >
                                                        {isSubActive && <div className="absolute left-0 w-1 h-6 bg-red-600 rounded-r-full"></div>}
                                                        <SubIcon size={16} />
                                                        <span>{subItem.name}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        // Regular Single Link
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.name}
                                to={item.path || '#'}
                                className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all duration-200 group relative overflow-hidden
                                    ${isActive 
                                        ? 'bg-white text-[#00635e] shadow-lg font-bold' 
                                        : 'hover:bg-white/10 text-teal-50'
                                    }
                                `}
                            >
                                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600"></div>}
                                <Icon size={20} className={isActive ? 'text-red-600' : ''} />
                                <span className="text-sm tracking-wide">{item.name}</span>
                            </Link>
                        );
                    })}
                </div>
            </div>

            {/* Support Box */}
            <div className="p-4 mb-20 md:mb-4">
                <div className="p-5 rounded-2xl bg-gradient-to-br from-teal-800 to-teal-900 border border-teal-700/50 shadow-inner">
                    <div className="flex items-center gap-2 mb-2 text-teal-200">
                        <LifeBuoy size={16} />
                        <span className="text-xs font-bold uppercase">Support</span>
                    </div>
                    <p className="text-sm font-bold text-white mb-3">Facing issues?</p>
                    <button className="w-full py-2.5 bg-white text-[#00635e] hover:bg-gray-100 rounded-lg text-xs font-bold transition-colors shadow-sm">
                        Chat with Manager
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default SellerSidebar;